/**
 * Custom React Hook for SEO Management
 * Automatically handles SEO updates when route changes
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import seoService from '../services/seoService';

/**
 * Custom hook for SEO management
 * @param {Object} customSEOData - Custom SEO data to override defaults
 * @param {Object} additionalTrackingData - Additional data for analytics tracking
 */
export const useSEO = (customSEOData = {}, additionalTrackingData = {}) => {
  const location = useLocation();

  useEffect(() => {
    // Update SEO for current page
    seoService.updatePageSEO(location.pathname, customSEOData);
    
    // Track page view for analytics
    seoService.trackPageView(location.pathname, additionalTrackingData);
    
    // Scroll to top for better UX
    window.scrollTo(0, 0);
  }, [location.pathname, customSEOData, additionalTrackingData]);

  return {
    currentPath: location.pathname,
    seoData: seoService.getPageSEOData(location.pathname)
  };
};

/**
 * Hook for blog post SEO
 * @param {Object} postData - Blog post data
 */
export const useBlogSEO = (postData) => {
  const location = useLocation();
  
  useEffect(() => {
    if (!postData) return;

    const blogSEOData = {
      title: `${postData.title} | LogCrest Blog`,
      description: postData.excerpt || postData.description,
      keywords: `${postData.tags?.join(', ') || ''}, observability, AI, DevOps, SRE, LogCrest blog`,
      canonical: `https://www.logcrest.com/blog/${postData.slug}`,
      ogType: 'article',
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: postData.title,
        description: postData.excerpt || postData.description,
        author: {
          '@type': 'Person',
          name: postData.author || 'LogCrest Team'
        },
        publisher: {
          '@type': 'Organization',
          name: 'LogCrest',
          logo: {
            '@type': 'ImageObject',
            url: 'https://www.logcrest.com/logo.png'
          }
        },
        datePublished: postData.publishedDate,
        dateModified: postData.updatedDate || postData.publishedDate,
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `https://www.logcrest.com/blog/${postData.slug}`
        },
        image: postData.featuredImage || 'https://www.logcrest.com/blog-default-image.png'
      }
    };

    seoService.updatePageSEO(location.pathname, blogSEOData);
    seoService.trackPageView(location.pathname, {
      post_title: postData.title,
      post_category: postData.category,
      post_author: postData.author
    });
  }, [location.pathname, postData]);

  return {
    currentPath: location.pathname,
    seoData: seoService.getPageSEOData(location.pathname)
  };
};

/**
 * Hook for product page SEO
 * @param {Object} productData - Product data
 */
export const useProductSEO = (productData) => {
  const location = useLocation();
  
  useEffect(() => {
    if (!productData) return;

    const productSEOData = {
      title: `${productData.name} - ${productData.tagline} | LogCrest`,
      description: productData.description,
      keywords: `${productData.keywords || ''}, observability, AI, DevOps, SRE, LogCrest`,
      canonical: `https://www.logcrest.com${location.pathname}`,
      ogType: 'product',
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: productData.name,
        description: productData.description,
        brand: {
          '@type': 'Brand',
          name: 'LogCrest'
        },
        category: productData.category || 'Software',
        offers: {
          '@type': 'Offer',
          availability: 'https://schema.org/InStock',
          priceCurrency: 'USD',
          price: productData.price || '0',
          url: `https://www.logcrest.com${location.pathname}`
        },
        aggregateRating: productData.rating ? {
          '@type': 'AggregateRating',
          ratingValue: productData.rating.value,
          reviewCount: productData.rating.count
        } : undefined
      }
    };

    seoService.updatePageSEO(location.pathname, productSEOData);
    seoService.trackPageView(location.pathname, {
      product_name: productData.name,
      product_category: productData.category
    });
  }, [location.pathname, productData]);

  return {
    currentPath: location.pathname,
    seoData: seoService.getPageSEOData(location.pathname)
  };
};

export default useSEO;
